/* eslint-disable max-lines-per-function, no-undefined */
import React from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import { updatePricing } from "types/gql-types";
import { AdminHeading } from "../shared/admin-form";
import { useAdminSubmit } from "../shared/use-admin-submit";
import PricingForm, { PricingFormType } from "./form";
import {
  UPDATE_PRICING,
  GET_PRICING_BY_ID,
  GET_PRICING_TYPES,
  GET_THRESHOLD_TYPES,
} from "./queries";
import { buildPricingMutationInput, mapPricingTiersToForm } from "./pricing-form-utils";

const UpdatePricing: React.FC = () => {
  const { pricingId } = useParams<{ pricingId: string }>();
  const { sending, submit } = useAdminSubmit("/dash/admin/pricing");
  const [updatePricingFunc] = useMutation<updatePricing>(UPDATE_PRICING, {
    refetchQueries: ["pricingAll", "pricingAllForPlans", "pricingAllForSubscriptions"],
  });
  const {
    loading,
    error,
    data: pricingData,
  } = useQuery(GET_PRICING_BY_ID, {
    variables: { id: pricingId },
    skip: !pricingId,
  });
  const { data: pricingTypesData } = useQuery(GET_PRICING_TYPES);
  const { data: thresholdTypesData } = useQuery(GET_THRESHOLD_TYPES);
  const submitForm = (formData: PricingFormType) => {
    if (!pricingId) return;

    submit(() =>
      updatePricingFunc({
        variables: { id: pricingId, data: buildPricingMutationInput(formData) },
        refetchQueries: ["pricingAll", "pricingAllForPlans", "pricingAllForSubscriptions"],
      }));
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!pricingData?.pricing) return <div>Pricing configuration not found</div>;

  const { pricing } = pricingData;
  const pricingTypes = pricingTypesData?.pricingTypes || [];
  const thresholdTypes = thresholdTypesData?.thresholdTypes || [];

  const initialData: PricingFormType = {
    pricingTypeId: pricing.pricingTypeId ? parseInt(pricing.pricingTypeId, 10) : undefined,
    thresholdTypeId: pricing.thresholdTypeId ? parseInt(pricing.thresholdTypeId, 10) : undefined,
    minimumPrice: pricing.minimumPrice || undefined,
    unitThreshold: pricing.unitThreshold || undefined,
    annualDiscountPercent: pricing.annualDiscountPercent || undefined,
    monthlyPricePerUnit: pricing.monthlyPricePerUnit || undefined,
    tiers: mapPricingTiersToForm(pricing.tiers),
  };

  return (
    <div>
      <AdminHeading>Update Pricing Configuration</AdminHeading>
      <PricingForm
        formType="update"
        initialData={initialData}
        sending={sending}
        handleSubmit={submitForm}
        pricingTypes={pricingTypes}
        thresholdTypes={thresholdTypes}
      />
    </div>
  );
};

export default UpdatePricing;
